import { useEffect, useRef, useState } from 'react'
import { useWindowSize } from 'usehooks-ts'

import { Filter, IconButton } from 'baby-design'
import { LeftArrow, MostWantedFilled, RightArrow } from 'baby-design/icons'
import { RegistryCategoryFilterOption } from 'src/types/registrySortFilterOptions'
import { RegistryCategoryFiltersProps } from './RegistryCategoryFilters.types'
import css from './RegistryCategoryFilters.styles.scss'
import RegistryCategoryFiltersSkeleton from './RegistryCategoryFilters.skeleton'
import {
  handleScrollClick,
  isLeftArrowVisible as getIsLeftArrowVisible,
  isRightArrowVisible as getIsRightArrowVisible,
} from './RegistryCategoryFilters.utils'

const RegistryCategoryFilters: React.FC<RegistryCategoryFiltersProps> = ({
  appliedSortsAndFilters,
  sortFilterOptions,
  onSortFilterChange,
}: RegistryCategoryFiltersProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const windowSize = useWindowSize()
  const [isLeftArrowVisible, setIsLeftArrowVisible] = useState(false)
  const [isRightArrowVisible, setIsRightArrowVisible] = useState(false)

  useEffect(() => {
    setIsLeftArrowVisible(getIsLeftArrowVisible(wrapperRef))
    setIsRightArrowVisible(getIsRightArrowVisible(wrapperRef))
  }, [wrapperRef, windowSize.width, sortFilterOptions])

  if (!sortFilterOptions) return <RegistryCategoryFiltersSkeleton />

  const selectedCategory = appliedSortsAndFilters.filters?.category
  const filterSize = windowSize.width < 768 ? 'lg' : 'xl'

  return (
    <div
      className={css.RegistryCategoryFilters}
      data-testid="RegistryCategoryFilters"
    >
      {isLeftArrowVisible && (
        <IconButton
          className={css.RegistryCategoryFilters__LeftArrow}
          data-testid="LeftArrow"
          size="md"
          variant="fill-secondary"
          onClick={() =>
            handleScrollClick({
              direction: 'left',
              wrapperRef,
              setIsLeftArrowVisible,
              setIsRightArrowVisible,
            })
          }
        >
          <LeftArrow />
        </IconButton>
      )}
      <div className={css.RegistryCategoryFilters__Wrapper} ref={wrapperRef}>
        {sortFilterOptions &&
          sortFilterOptions?.filters?.category?.options.map(
            (categoryOption: RegistryCategoryFilterOption) => (
              <Filter
                LeftIcon={
                  categoryOption.name === 'Most wanted' ? (
                    <span data-testid="MostWantedIcon">
                      <MostWantedFilled
                        className={css.RegistryCategoryFilters__MostWantedIcon}
                      />
                    </span>
                  ) : undefined
                }
                className={css.RegistryCategoryFilters__CategoryFilter}
                isPressed={
                  selectedCategory === categoryOption.value ||
                  selectedCategory === categoryOption.name
                }
                key={`${categoryOption.value}-${categoryOption.name}`}
                overrideIconColor={false}
                size={filterSize}
                type="secondary"
                variant="fill"
                onClick={() =>
                  onSortFilterChange({
                    filters: {
                      category: categoryOption.value || categoryOption.name,
                    },
                  })
                }
              >
                {`${categoryOption.name} (${categoryOption.count})`}
              </Filter>
            )
          )}
      </div>
      {isRightArrowVisible && (
        <IconButton
          className={css.RegistryCategoryFilters__RightArrow}
          data-testid="RightArrow"
          size="md"
          variant="fill-secondary"
          onClick={() =>
            handleScrollClick({
              direction: 'right',
              wrapperRef,
              setIsLeftArrowVisible,
              setIsRightArrowVisible,
            })
          }
        >
          <RightArrow />
        </IconButton>
      )}
    </div>
  )
}

export default RegistryCategoryFilters
